import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, TextField, Button, Grid, Snackbar, Alert, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  useTheme, useMediaQuery
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import SaveIcon from '@mui/icons-material/SaveOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrowsOutlined';
import api from '../api';

const Settings = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState({
    managerName: '',
    managerAddress: '',
    managerCityState: '',
    managerZip: '',
    managerEmail: '',
    managerPhone: '',
    companyName: 'ZenRentFlow',
    jurisdiction: '', // Province/State for privacy act reference
    screeningCriteria: {
      minIncomeMultiplier: 3,
      minCreditScore: 650,
      evictionPolicy: 'deny',
      allowPets: true,
      allowSmoking: false,
      riskTolerance: 'med'
    },
    backgroundCheckConfig: {
      provider: 'frontlobby',
      defaultCountry: 'CA',
      autoRunOnConsent: false,
      costPerCheck: 0
    },
    screeningSetup: {
      allowProviderSelection: false,
      defaultProviderByCountry: { CA: 'mock', US: 'mock' },
      showProviderComparisonLink: false,
      displayPricingMode: 'ZENRENFLOW_PRICE',
      enableCreditRail: false
    }
  });
  const [availableProviders, setAvailableProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await api.get('/api/billing/providers');
        setAvailableProviders(Array.isArray(res.data) ? res.data : []);
      } catch (_) {
        setAvailableProviders([]);
      }
    };
    fetchProviders();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/settings');
      const data = response.data || {};
      setSettings({
        ...settings,
        ...data,
        screeningCriteria: {
          minIncomeMultiplier: 3,
          minCreditScore: 650,
          evictionPolicy: 'deny',
          allowPets: true,
          allowSmoking: false,
          riskTolerance: 'med',
          ...(data.screeningCriteria || {})
        },
        backgroundCheckConfig: {
          provider: 'frontlobby',
          defaultCountry: 'CA',
          autoRunOnConsent: false,
          costPerCheck: 0,
          ...(data.backgroundCheckConfig || {})
        },
        screeningSetup: {
          allowProviderSelection: false,
          defaultProviderByCountry: { CA: 'mock', US: 'mock' },
          showProviderComparisonLink: false,
          displayPricingMode: 'ZENRENFLOW_PRICE',
          enableCreditRail: false,
          ...(data.screeningSetup || {})
        }
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to fetch settings', 
        severity: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setSettings({
      ...settings,
      [field]: e.target.value
    });
  };

  const handleCriteriaChange = (field) => (e) => {
    setSettings({
      ...settings,
      screeningCriteria: {
        ...settings.screeningCriteria,
        [field]: e.target.value
      }
    });
  };

  const handleCriteriaToggle = (field) => (e) => {
    setSettings({
      ...settings,
      screeningCriteria: {
        ...settings.screeningCriteria,
        [field]: e.target.checked
      }
    });
  };

  const handleScreeningSetupToggle = (field) => (e) => {
    setSettings({
      ...settings,
      screeningSetup: {
        ...settings.screeningSetup,
        [field]: e.target.checked
      }
    });
  };

  const handleScreeningSetupSelect = (field) => (e) => {
    setSettings({
      ...settings,
      screeningSetup: {
        ...settings.screeningSetup,
        [field]: e.target.value
      }
    });
  };

  const handleScreeningSetupCountry = (country) => (e) => {
    setSettings({
      ...settings,
      screeningSetup: {
        ...settings.screeningSetup,
        defaultProviderByCountry: {
          ...(settings.screeningSetup?.defaultProviderByCountry || { CA: 'mock', US: 'mock' }),
          [country]: e.target.value
        }
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/api/settings', settings);
      setSnackbar({ 
        open: true, 
        message: 'Settings saved successfully!', 
        severity: 'success' 
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({ 
        open: true, 
        message: 'Failed to save settings', 
        severity: 'error' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'flex-start', sm: 'center' }, 
        mb: { xs: 2, sm: 3 },
        gap: { xs: 2, sm: 0 }
      }}>
        <Typography 
          variant="h4" 
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, mb: { xs: 0, sm: 0 } }}
        >
          <SettingsIcon sx={{ mr: { xs: 0.5, sm: 1 }, verticalAlign: 'middle', fontSize: { xs: 24, sm: 28 } }} />
          {isMobile ? 'Settings' : 'Property Manager Settings'}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          fullWidth={isMobile}
          size={isMobile ? 'medium' : 'medium'}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>

      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}
      >
        Configure your property management information. This will be used in notice headers and official documents.
      </Typography>

      <Card>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            Contact Information
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Manager/Company Name"
                value={settings.managerName}
                onChange={handleChange('managerName')}
                placeholder="Your Name or Company Name"
                helperText="This will appear as the sender name on notices"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company Name"
                value={settings.companyName}
                onChange={handleChange('companyName')}
                placeholder="ZenRentFlow"
                helperText="Your business or company name"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                value={settings.managerAddress}
                onChange={handleChange('managerAddress')}
                placeholder="123 Main Street"
                helperText="Your business address"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="City, State"
                value={settings.managerCityState}
                onChange={handleChange('managerCityState')}
                placeholder="Los Angeles, CA"
                helperText="City and state"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Zip Code"
                value={settings.managerZip}
                onChange={handleChange('managerZip')}
                placeholder="90001"
                helperText="Postal code"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={settings.managerEmail}
                onChange={handleChange('managerEmail')}
                placeholder="manager@example.com"
                helperText="Contact email for notices"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={settings.managerPhone}
                onChange={handleChange('managerPhone')}
                placeholder="(555) 123-4567"
                helperText="Contact phone number"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Province/State (Jurisdiction)"
                value={settings.jurisdiction}
                onChange={handleChange('jurisdiction')}
                placeholder="Alberta, CA, NY, etc."
                helperText="Used for privacy act references (e.g., Alberta, California)"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
          >
            Tenant Screening Criteria
          </Typography>

          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Minimum Income Multiplier"
                type="number"
                value={settings.screeningCriteria?.minIncomeMultiplier ?? 3}
                onChange={handleCriteriaChange('minIncomeMultiplier')}
                helperText="Example: 3 means income should be 3x rent"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Minimum Credit Score"
                type="number"
                value={settings.screeningCriteria?.minCreditScore ?? 650}
                onChange={handleCriteriaChange('minCreditScore')}
                helperText="Used for guidance until scoring integrations are added"
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Eviction Policy</InputLabel>
                <Select
                  label="Eviction Policy"
                  value={settings.screeningCriteria?.evictionPolicy || 'deny'}
                  onChange={handleCriteriaChange('evictionPolicy')}
                >
                  <MenuItem value="deny">Deny if eviction disclosed</MenuItem>
                  <MenuItem value="review">Review case-by-case</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Risk Tolerance</InputLabel>
                <Select
                  label="Risk Tolerance"
                  value={settings.screeningCriteria?.riskTolerance || 'med'}
                  onChange={handleCriteriaChange('riskTolerance')}
                >
                  <MenuItem value="low">Low (more strict)</MenuItem>
                  <MenuItem value="med">Medium</MenuItem>
                  <MenuItem value="high">High (more flexible)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.screeningCriteria?.allowPets ?? true}
                    onChange={handleCriteriaToggle('allowPets')}
                  />
                }
                label="Allow Pets"
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.screeningCriteria?.allowSmoking ?? false}
                    onChange={handleCriteriaToggle('allowSmoking')}
                  />
                }
                label="Allow Smoking"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: { xs: 2, sm: 3 } }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Typography variant="h6" gutterBottom sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            Tenant Screening Setup
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Default screening provider by country and whether to show &quot;Compare providers&quot; when requesting screening.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button component={Link} to="/screening/compare" variant="outlined" size="small" startIcon={<CompareArrowsIcon />}>
              Open Compare providers page
            </Button>
            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
              Use with applicationId and paidBy in the URL when creating an order from Request Screening (e.g. /screening/compare?applicationId=xxx&paidBy=applicant).
            </Typography>
          </Box>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Default provider (Canada)</InputLabel>
                <Select
                  label="Default provider (Canada)"
                  value={settings.screeningSetup?.defaultProviderByCountry?.CA ?? 'mock'}
                  onChange={handleScreeningSetupCountry('CA')}
                >
                  {availableProviders.filter(p => p.countriesSupported?.includes('CA')).map(p => (
                    <MenuItem key={p.id} value={p.provider}>{p.displayName || p.provider}</MenuItem>
                  ))}
                  {availableProviders.length === 0 && <MenuItem value="mock">Mock (dev)</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Default provider (US)</InputLabel>
                <Select
                  label="Default provider (US)"
                  value={settings.screeningSetup?.defaultProviderByCountry?.US ?? 'mock'}
                  onChange={handleScreeningSetupCountry('US')}
                >
                  {availableProviders.filter(p => p.countriesSupported?.includes('US')).map(p => (
                    <MenuItem key={p.id} value={p.provider}>{p.displayName || p.provider}</MenuItem>
                  ))}
                  {availableProviders.length === 0 && <MenuItem value="mock">Mock (dev)</MenuItem>}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                <InputLabel>Price display</InputLabel>
                <Select
                  label="Price display"
                  value={settings.screeningSetup?.displayPricingMode ?? 'ZENRENFLOW_PRICE'}
                  onChange={handleScreeningSetupSelect('displayPricingMode')}
                >
                  <MenuItem value="ZENRENFLOW_PRICE">ZenRentFlow price</MenuItem>
                  <MenuItem value="PROVIDER_ESTIMATE">Provider estimate</MenuItem>
                  <MenuItem value="VARIES_ONLY">Varies only</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.screeningSetup?.showProviderComparisonLink ?? false}
                    onChange={handleScreeningSetupToggle('showProviderComparisonLink')}
                  />
                }
                label="Show &quot;Compare providers&quot; link"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.screeningSetup?.allowProviderSelection ?? false}
                    onChange={handleScreeningSetupToggle('allowProviderSelection')}
                  />
                }
                label="Allow provider selection (multi-provider mode)"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.screeningSetup?.enableCreditRail ?? false}
                    onChange={handleScreeningSetupToggle('enableCreditRail')}
                  />
                }
                label="Enable credit rail (e.g. TransUnion ShareAble)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;






